'use client'

import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import type { ClientMembership, PendingRequest, AvailableInvitation } from './types'
import { notify } from '@/lib/notifications';

interface OverviewTabProps {
  clientMemberships: ClientMembership[]
  pendingRequests: PendingRequest[]
  availableInvitations: AvailableInvitation[]
  loadingClients: boolean
  currentClientId?: number
  onOpenOnboarding: () => void
}

export function OverviewTab({
  clientMemberships,
  pendingRequests,
  availableInvitations,
  loadingClients,
  currentClientId,
  onOpenOnboarding
}: OverviewTabProps) {
  return (
    <div className="space-y-6">
      {/* Current Client Memberships */}
      <div>
        <h3 className="text-lg font-semibold pm-text-primary mb-4">Your Organizations</h3>
        {loadingClients ? (
          <div className="flex items-center justify-center py-8">
            <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
          </div>
        ) : clientMemberships.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {clientMemberships.map((membership) => (
              <div key={membership.id} className="p-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h4 className="font-medium pm-text-primary">{membership.name}</h4>
                    {membership.trading_name && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">{membership.trading_name}</p>
                    )}
                    <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">{membership.role}</p>
                    {currentClientId !== undefined && membership.id === currentClientId && (
                      <p className="text-xs text-[var(--theme-primary)] font-medium">Current Organization</p>
                    )}
                    {membership.is_default && (
                      <p className="text-xs text-yellow-600 font-medium">Default Organization</p>
                    )}
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${membership.membership_status === 'active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : membership.membership_status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                    {membership.membership_status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <p>You are not currently a member of any organizations.</p>
            <p className="text-sm">Use the dashboard to join an organization.</p>
          </div>
        )}
      </div>

      {/* Pending Join Requests - Always show section */}
      <div>
        <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold pm-text-primary">Pending Join Requests</h3>
          <button
            onClick={onOpenOnboarding}
            className="px-4 py-2 bg-[var(--theme-primary)] text-white text-sm rounded-lg hover:bg-[var(--theme-primary-dark)] transition-colors flex items-center justify-center sm:w-auto"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Onboard with Client
          </button>
        </div>
        {pendingRequests.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {pendingRequests.map((request) => (
              <div key={request.id} className="p-4 border border-yellow-200 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium pm-text-primary">{request.name}</h4>
                    {request.trading_name && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">{request.trading_name}</p>
                    )}
                    <p className="text-sm text-gray-600 dark:text-gray-400">Awaiting approval</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Requested: {new Date(request.requested_at).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-[var(--theme-primary)] font-medium">
                      Reference #: {request.id}
                    </p>
                  </div>
                  <div className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 flex-shrink-0">
                    Pending
                  </div>
                </div>
                {request.request_details && (
                  <div className="mt-2 pt-2 border-t border-yellow-200 dark:border-yellow-700">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Details:</span> {request.request_details}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>No pending join requests.</p>
            <p className="text-sm">Use the &quot;Onboard with Client&quot; button to join a new organization.</p>
          </div>
        )}
      </div>

      {/* Available Invitations */}
      {availableInvitations.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold pm-text-primary mb-4">Available Invitations</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {availableInvitations.map((invitation) => (
              <div key={invitation.id} className="p-4 border border-[rgba(var(--primary-teal-rgb),0.35)] dark:border-[rgba(var(--primary-teal-rgb),0.4)] bg-[rgba(var(--primary-teal-rgb),0.08)] dark:bg-[rgba(var(--primary-teal-rgb),0.16)] rounded-lg">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h4 className="font-medium pm-text-primary">{invitation.client_name}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Invited by {invitation.invited_by_name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Expires: {new Date(invitation.expires_at).toLocaleDateString()}
                    </p>
                    {invitation.role && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 capitalize">Role: {invitation.role}</p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      // Copy invite code to clipboard and show onboarding modal
                      navigator.clipboard.writeText(invitation.invite_code)
                      notify.success(`Invite code ${invitation.invite_code} copied to clipboard. Use it in the dashboard to join ${invitation.client_name}.`)
                    }}
                    className="px-3 py-1 bg-[var(--theme-primary)] text-white text-sm rounded hover:bg-[var(--theme-primary-dark)] transition-colors flex-shrink-0"
                  >
                    Use Code
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
